/**
 * Six digits, typed.
 *
 * ONE INPUT, NOT SIX BOXES, and that is a deliberate reversal of the obvious
 * design. Six separate boxes look right and behave badly: they fight password
 * managers, they break paste, backspace at the start of a box has to be
 * special-cased, and screen readers announce six unlabelled fields. A single
 * field spaced out with CSS reads the same to a person and behaves like a
 * field to everything else.
 *
 * `type="password"` because a child is often standing next to the parent
 * typing it, which is the whole situation this PIN exists for.
 *
 * `inputMode="numeric"` puts the number pad up on a phone without making this
 * `type="number"`, which would bring spinners, allow `e` and `-`, and strip
 * leading zeros -- and a PIN is a string of digits, not a quantity.
 */

import { useEffect, useRef } from 'react';
import { PIN_LENGTH } from '../auth/pin';

export function PinEntry({
  value,
  onChange,
  onComplete,
  label,
  autoFocus = false,
  disabled = false,
}: {
  value: string;
  onChange: (value: string) => void;
  /** Called once the sixth digit lands, so nobody has to press a button. */
  onComplete?: (value: string) => void;
  label: string;
  autoFocus?: boolean;
  disabled?: boolean;
}) {
  const field = useRef<HTMLInputElement>(null);
  // Guards against firing twice for one completed PIN: the effect below runs
  // on every render where `value` is six long, including the re-render caused
  // by whatever `onComplete` did.
  const fired = useRef(false);

  useEffect(() => {
    if (autoFocus) field.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (value.length !== PIN_LENGTH) {
      fired.current = false;
      return;
    }
    if (fired.current) return;
    fired.current = true;
    onComplete?.(value);
  }, [value, onComplete]);

  return (
    <>
      <label className="field-label" htmlFor="pin-entry">
        {label}
      </label>
      <input
        id="pin-entry"
        ref={field}
        className="pin-entry"
        type="password"
        inputMode="numeric"
        autoComplete="off"
        // Off, all four. A PIN is not a word and every one of these turns a
        // six-digit field into a fight with the keyboard.
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        maxLength={PIN_LENGTH}
        disabled={disabled}
        value={value}
        onChange={(event) => {
          // Strip anything that is not a digit rather than rejecting the
          // keystroke. Paste from a password manager arrives with spaces in
          // it often enough to be worth handling, and a rejected paste gives
          // a person nothing to correct.
          onChange(event.target.value.replace(/\D/g, '').slice(0, PIN_LENGTH));
        }}
      />
    </>
  );
}
